<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import BaseComment from '@/components/base/BaseComment.vue';
import ReorderableTable from '@/components/ReorderableTable.vue';
import SummaryTableWrapper from '@/components/ConsultationSummary/SummaryTableWrapper.vue';

import { useSummariesStore } from '@/stores/summaries.store';
import type { Locales, Pagination, SummaryFormOpenVoteDto } from '@/services/types/api.types';

// STORE
const summariesStore = useSummariesStore();
const { formOpenVotes } = storeToRefs(summariesStore);

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  formId: number;
}>();

// CONST
const { t } = useI18n();

// LIFECYCLE
onMounted(async () => {
  await summariesStore.getFormOpenVotes(props.formId);
});

// COMPUTED
const columns = computed(() => [
  {
    name: 'answer',
    label: t('form.summary.results.responses'),
    align: 'left',
    field: (row: SummaryFormOpenVoteDto) => row.text,
  },
]);

const hasOpenVotes = computed(() => {
  const votes = formOpenVotes?.value?.get(props.formId);
  return votes?.data && votes.data.length > 0;
});

// FUNCTIONS
const fetchOpenVotes = async (request: Pagination) => {
  await summariesStore.getFormOpenVotes(props.formId, request.page, request.rowsPerPage);
};
</script>
<template>
  <div class="form-open-question-results">
    <div v-if="hasOpenVotes" class="form-open-question-results__table">
      <SummaryTableWrapper>
        <template v-slot>
          <reorderable-table
            :rows="formOpenVotes.get(props.formId)?.data || []"
            :columns="columns"
            :loading="summariesStore.isFormOpenVotesLoading(props.formId)"
            itemKey="id"
            :disable-add-button="true"
            :disable-delete-button="true"
            :disableOrderable="true"
            :pagination-enabled="true"
            :pagination="formOpenVotes.get(props.formId)?.pagination"
            @request="fetchOpenVotes"
          >
            <template v-slot:body-cell-answer="props">
              <q-td :props="props">
                <BaseComment :user-response="props.row" :text="props.row.text" />
              </q-td>
            </template>
          </reorderable-table>
        </template>
      </SummaryTableWrapper>
    </div>
    <div v-else class="form-open-question-results__no-open-votes">
      <div class="title">
        {{ t('form.summary.results.responses') }}
      </div>
      <div class="text">
        {{ t('form.summary.status.no-votes') }}
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.form-open-question-results {
  .base-comment {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: $color-neutral-grey-200;
    border-radius: 6px;
    padding: $space-md;
    margin: 0;
  }

  &__no-open-votes {
    .title {
      font-weight: bold;
      font-size: $sub-header;
      margin: 0 0 20px 0 !important;
      padding: 0 !important;
    }

    .text {
      color: $color-neutral-grey-400;
      font-size: $small-text;
      padding: 0 0 $space-md 0;
    }
  }

  .summary-table-wrapper > .q-table__container > .q-table__middle > .q-table > thead > tr > .text-left {
    padding: 0 0 $space-md 0 !important;
  }

  .summary-table-wrapper > .q-table__container > .q-table__middle > .q-table > thead > tr {
    height: auto !important;
  }

  .summary-table-wrapper > .q-table__container > .q-table__middle > .q-table > tbody {
    border-top: 8px solid $color-neutral-white !important;
  }
  .summary-table-wrapper > .q-table__container > .q-table__middle > .q-table {
    border-spacing: 0 !important;
  }

  .summary-table-wrapper > .q-table__container > .q-table__middle > .q-table > tbody > tr > td {
    padding-top: $space-xs !important;
    padding-bottom: $space-xs !important;
  }

  .q-table__container > .q-table__middle > .q-table > tbody > tr > td:before {
    background-color: transparent;
  }

  .summary-table-wrapper > .q-table__container > .q-table__middle > .q-table > tbody > tr > td > .base-comment:hover {
    background-color: $color-neutral-grey-300 !important;
    color: $color-neutral-black-text;
  }
}
</style>
